import { useId } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import { Box, Button, Drawer, IconButton, Stack, Typography } from '@mui/material';
import type { SystemStyleObject, Theme } from '@mui/system';

import type { GeneratedIntakeHelpPanel } from '../../types';

import { HelpMarkdown } from './HelpPanelDrawer.styles';
import { groupBlurbSx } from './tokens';

interface HelpPanelDrawerProps {
  drawerId: string;
  panel: GeneratedIntakeHelpPanel | null;
  open: boolean;
  onClose: () => void;
  onAction: (action: GeneratedIntakeHelpPanel['support_cta']['action']) => void;
}

export function HelpPanelDrawer({
  drawerId,
  panel,
  open,
  onClose,
  onAction,
}: HelpPanelDrawerProps) {
  const id = useId().replace(/:/g, '');
  const titleId = `intake-help-title-${id}`;
  const definitionId = `intake-help-definition-${id}`;

  if (!panel) return null;

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          id: drawerId,
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': titleId,
          'aria-describedby': definitionId,
          sx: { width: { xs: 'min(100vw, 420px)', sm: 420 }, p: 3 },
        },
      }}
    >
      <Stack sx={{ height: '100%' }} spacing={2}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Typography id={titleId} component="h2" variant="h6" sx={{ fontSize: '1.5rem' }}>
            {panel.title}
          </Typography>
          <IconButton autoFocus aria-label="Close help" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box id={definitionId}>
          <HelpMarkdown size="medium" markdown={panel.definition} />
        </Box>

        {(panel.what_goes_wrong?.length ?? 0) > 0 && (
          <Box>
            <Typography component="h3" variant="subtitle2" sx={{ mb: 0.5, fontSize: '1rem' }}>
              What can go wrong
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
              {panel.what_goes_wrong?.map((item, index) => (
                <Box component="li" key={`${panel.key}-pitfall-${index}`}>
                  <HelpMarkdown size="medium" markdown={item} />
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {panel.where_to_find && (
          <Box>
            <Typography component="h3" variant="subtitle2" sx={{ mb: 0.5, fontSize: '1rem' }}>
              Where to find it
            </Typography>
            <HelpMarkdown size="medium" markdown={panel.where_to_find} />
          </Box>
        )}

        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={() => {
            onClose();
            onAction(panel.support_cta.action);
          }}
        >
          {panel.support_cta.label}
        </Button>
      </Stack>
    </Drawer>
  );
}

/**
 * The group's blurb: one line of always-visible context under the group header,
 * as distinct from the help panel behind the "?" trigger. Renders nothing when
 * the catalog has no blurb for the group.
 */
export function GroupBlurb({
  blurb,
  component = 'div',
  sx,
}: {
  blurb?: string | null;
  component?: 'div' | 'span';
  sx?: SystemStyleObject<Theme>;
}) {
  if (!blurb) return null;
  return (
    <Box component={component} data-testid="group-blurb" sx={{ ...groupBlurbSx, ...sx }}>
      {blurb}
    </Box>
  );
}

interface GroupHelpTriggerProps {
  groupName: string;
  controlsId?: string;
  expanded: boolean;
  onOpen: (opener: HTMLButtonElement) => void;
}

export function GroupHelpTrigger({
  groupName,
  controlsId,
  expanded,
  onOpen,
}: GroupHelpTriggerProps) {
  return (
    <IconButton
      size="small"
      aria-label={`Help for ${groupName}`}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-controls={expanded ? controlsId : undefined}
      onClick={event => onOpen(event.currentTarget)}
    >
      <HelpOutlineIcon fontSize="small" />
    </IconButton>
  );
}
